import React, { useMemo, useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { createRoot } from "react-dom/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import "../assets/css/programmebiodata.css";
import AdminNavBar from "../components/AdminNavbar";
import PrintPage from "../components/PrintPage";

const isProduction = process.env.NODE_ENV === "production";
const API_BASE_URL = isProduction
  ? "https://schools.luth.gov.ng"
  : "http://localhost:3003";

const gradeOptions = ["A1", "B2", "B3", "C4", "C5", "C6", "D7", "E8", "F9"];

const programmes = [
  "Department of Nursing Sciences",
  "Biomedical Engineering (ND)",
  "Biomedical Engineering (HND)",
  "Anaesthetic Technicians",
  "School of Health Information Management (ND)",
  "School of Health Information Management (HND)",
  "School of Medical and Psychiatry Social Work",
  "School of Community Health",
  "School of Community Health II",
  "Accident & Emergency",
  "Critical Care Nursing",
  "Neonatal Nursing",
  "Nephrology Nursing",
  "Ophthalmic Nursing",
  "Paediatric Nursing",
  "Perioperative Nursing",
  "School Of Post Basic Nursing",
  "Dental Nursing",
];

const PAGE_SIZE = 8;

export default function AdminDashboard() {
  const [filter, setFilter] = useState(programmes[0]);
  const [biodata, setBiodata] = useState([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [editing, setEditing] = useState(null);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedYear, setSelectedYear] = useState("All Years");
  const [admittingId, setAdmittingId] = useState(null);

  useEffect(() => {
    fetchBiodata();
    setPageIndex(0);
    setSearchTerm("");
    setSearchInput("");
    setSelectedYear("All Years");
  }, [filter]);

  async function fetchBiodata() {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/checkappbiodata?programme=${encodeURIComponent(
          filter,
        )}`,
      );
      const json = await res.json();
      setBiodata(Array.isArray(json) ? json : json.data || []);
    } catch (err) {
      console.error(err);
    }
  }

  const availableYears = useMemo(() => {
    const years = biodata
      .map((item) => item.ApplicationYear)
      .filter((y) => y !== null && y !== undefined && y !== "")
      .map((y) => String(y).trim());

    return [
      "All Years",
      ...Array.from(new Set(years)).sort((a, b) => Number(b) - Number(a)),
    ];
  }, [biodata]);

  const yearFiltered = useMemo(() => {
    if (selectedYear === "All Years") return biodata;
    return biodata.filter(
      (item) =>
        String(item.ApplicationYear || "").trim() ===
        String(selectedYear).trim(),
    );
  }, [biodata, selectedYear]);

  const filtered = useMemo(() => {
    if (!searchTerm) return yearFiltered;

    const term = searchTerm.toLowerCase();
    return yearFiltered.filter((item) => {
      const fullName =
        `${item.FirstName || ""} ${item.LastName || ""} ${item.MiddleName || ""}`.toLowerCase();
      return (
        fullName.includes(term) ||
        (item.Email || "").toLowerCase().includes(term) ||
        (item.ApplicationNo || "").toLowerCase().includes(term)
      );
    });
  }, [yearFiltered, searchTerm]);

  const start = pageIndex * PAGE_SIZE;
  const paged = filtered.slice(start, start + PAGE_SIZE);
  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);

  function handleSearch() {
    setPageIndex(0);
    setSearchTerm(searchInput.trim());
  }

  async function openEdit(item) {
    setLoadingEdit(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/checkappbiodata/${item.Id}`);
      if (!res.ok) throw new Error("Load error");
      const full = await res.json();
      setEditing(full);
    } catch (err) {
      console.error(err);
      alert("Failed to load biodata for editing.");
    } finally {
      setLoadingEdit(false);
    }
  }

  function cancelEdit() {
    setEditing(null);
  }

  async function saveEdit() {
    try {
      const { Id, ApplicationNo, ...upd } = editing;

      await fetch(`${API_BASE_URL}/api/checkappbiodata/${Id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...upd,
          AdmissionStatus: upd.AdmissionStatus,
          institutions: Array.isArray(upd.institutions) ? upd.institutions : [],
          exams: Array.isArray(upd.exams)
            ? upd.exams.map((e) => ({
                ExamName: e.ExamName,
                SubjectsAndGrades: e.SubjectsAndGrades,
              }))
            : [],
        }),
      });

      setEditing(null);
      fetchBiodata();
    } catch (err) {
      console.error(err);
      alert("Failed to save changes.");
    }
  }

  async function admitStudent(id) {
    const ok = window.confirm("Mark this applicant as Admitted?");
    if (!ok) return;

    setAdmittingId(id);
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/checkappbiodata/${id}/admit`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ AdmissionStatus: "Admitted" }),
        },
      );

      if (!res.ok) {
        const msg = await res.json().catch(() => ({}));
        throw new Error(msg.message || "Failed to admit student.");
      }

      await fetchBiodata();
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to admit student.");
    } finally {
      setAdmittingId(null);
    }
  }

  function exportExcel() {
    const gradeMap = {
      A1: 4.0,
      B2: 3.6,
      B3: 3.2,
      C4: 2.8,
      C5: 2.4,
      C6: 2.0,
      D7: 1.6,
      E8: 1.2,
      F9: 0.8,
    };

    const gradeValue = (g) => (g ? gradeMap[String(g).toUpperCase()] || 0 : 0);

    const mergedRows = filtered.flatMap((rec) => {
      const insts = rec.institutions || [];
      const examsRaw = rec.exams || [];

      const exams = examsRaw.map((ex) => {
        let s = ex.SubjectsAndGrades || {};
        if (typeof s === "string") {
          try {
            s = JSON.parse(s);
          } catch {
            s = {};
          }
        }
        return { ...ex, SubjectsAndGrades: s || {} };
      });

      let bestSubjects = {};
      if (exams.length === 1) {
        const { SubjectsAndGrades } = exams[0];
        const { year, ...subs } = SubjectsAndGrades;
        bestSubjects = { ...subs };
      } else {
        for (const ex of exams) {
          const s = ex.SubjectsAndGrades || {};
          for (const [subject, grade] of Object.entries(s)) {
            if (subject === "year") continue;
            const existing = bestSubjects[subject];
            if (!existing || gradeValue(grade) > gradeValue(existing)) {
              bestSubjects[subject] = grade;
            }
          }
        }
      }

      const jambScore = Number(rec.JambScore) || 0;
      const jambTotal = jambScore / 8;

      return insts.map((inst) => {
        const gradeTotal = Object.values(bestSubjects).reduce((sum, letter) => {
          return sum + (gradeMap[String(letter).toUpperCase()] || 0);
        }, 0);

        const total = jambTotal + gradeTotal;

        const examNames = exams.map((e) => e.ExamName).filter(Boolean);
        const combinedExamName =
          examNames.length > 1
            ? `Combined (${examNames.join(" / ")})`
            : examNames[0] || "";

        const years = exams
          .map((e) => e.SubjectsAndGrades && e.SubjectsAndGrades.year)
          .filter(Boolean)
          .map((y) => String(y).trim());

        const yearForExport = years.length ? years.sort().slice(-1)[0] : "";

        return {
          BiodataId: rec.Id,
          ApplicationNo: rec.ApplicationNo,
          FirstName: rec.FirstName,
          LastName: rec.LastName,
          Email: rec.Email,
          Phone: rec.Phone,
          JambRegNo: rec.JambRegNo,
          JambScore: jambScore,
          JambTotal: jambTotal,
          InstitutionName: inst.InstitutionName,
          QualificationObtained: inst.QualificationObtained,
          StartYear: inst.StartYear,
          DateOfBirth: rec.DateOfBirth,
          State: rec.State,
          EndYear: inst.EndYear,
          ExamName: combinedExamName,
          Year: yearForExport,
          AdmissionStatus: rec.AdmissionStatus || "Admission Processing",
          ...bestSubjects,
          GradeTotal: gradeTotal,
          Total: total,
        };
      });
    });

    const seenAppNos = new Set();
    const dedupedRows = [];
    mergedRows.forEach((row) => {
      const raw = row.ApplicationNo;
      const norm = raw ? String(raw).trim().toUpperCase() : "";
      const key = norm !== "" ? norm : `__ID__${row.BiodataId}`;
      if (!seenAppNos.has(key)) {
        seenAppNos.add(key);
        dedupedRows.push(row);
      }
    });

    const fixedCols = [
      "BiodataId",
      "ApplicationNo",
      "FirstName",
      "LastName",
      "Email",
      "Phone",
      "JambRegNo",
      "JambScore",
      "JambTotal",
      "DateOfBirth",
      "State",
      "InstitutionName",
      "QualificationObtained",
      "StartYear",
      "EndYear",
      "ExamName",
      "Year",
      "AdmissionStatus",
    ];

    const subjectCols = Array.from(
      dedupedRows.reduce((set, row) => {
        Object.keys(row).forEach((k) => {
          if (!fixedCols.includes(k) && k !== "GradeTotal" && k !== "Total") {
            set.add(k);
          }
        });
        return set;
      }, new Set()),
    ).sort();

    const headers = [...fixedCols, ...subjectCols, "GradeTotal", "Total"];
    const ws = XLSX.utils.json_to_sheet(dedupedRows, { header: headers });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "AllData");

    const fileName = `${filter.replace(/\W+/g, "_")}_AllData.xlsx`;
    XLSX.writeFile(wb, fileName);
  }

  async function handlePrint(record) {
    const css = `
      body { font-family: 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif; margin: 0; font-size: 12px; }
      .watermark { display: block; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.1; width: 300px; z-index: -1; }
      .logo-print { width: 80px; height: auto; object-fit: cover; border: 1px solid #ccc; }
      .section-title { font-weight: bold; margin-top: 20px; font-size: 1.2rem; border-bottom: 2px solid #ccc; padding-bottom: 5px; }
      .print-area { position: relative; }
      .print-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; }
      .print-title { color: green; text-align: center; font-size: 1.1rem; margin-bottom: 0.2rem; }
      .print-subtitle { color: #333; text-align: center; font-size: 1rem; margin-bottom: 0.2rem; }
      .print-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 0.6rem; margin-bottom: 1rem; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 1rem; }
      td, th { padding: 6px; vertical-align: top; border: 1px solid #ddd; }
      .passport-print { float: right; width: 120px; height: 140px; object-fit: cover; border: 1px solid #ccc; }
      p.note { margin-top: 1.5rem; font-style: italic; }
    `;

    const res = await fetch(`${API_BASE_URL}/api/checkappbiodata/${record.Id}`);
    const full = await res.json();

    const win = window.open("", "_blank", "width=900,height=700");
    win.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Print Preview</title>
          <style>${css}</style>
        </head>
        <body>
          <div id="print-root"></div>
        </body>
      </html>
    `);
    win.document.close();
    win.focus();

    setTimeout(() => {
      const root = createRoot(win.document.getElementById("print-root"));
      root.render(<PrintPage biodata={full} />);
      setTimeout(() => {
        win.print();
        win.close();
      }, 300);
    }, 100);
  }

  const totalApplicants = biodata.length;
  const totalFiltered = filtered.length;
  const totalOnThisPage = paged.length;

  return (
    <div style={{ marginTop: "-80px" }}>
      <AdminNavBar />
      <div className="programme-biodata-page">
        <aside className="sidebar">
          <h2>Programmes</h2>
          {programmes.map((p) => (
            <button
              key={p}
              className={p === filter ? "active" : ""}
              onClick={() => setFilter(p)}
            >
              {p}
            </button>
          ))}
        </aside>

        <main className="content">
          <div className="toolbar">
            <div>
              <h1>Applicant Biodata</h1>
              <p style={{ margin: 0, color: "#666" }}>
                Total: {totalApplicants} | Filtered: {totalFiltered} | Page:{" "}
                {totalOnThisPage}
              </p>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
                flexWrap: "wrap",
              }}
            >
              <div className="search-bar">
                <input
                  type="text"
                  placeholder="Search name, email or app no"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                  style={{
                    padding: "0.5rem",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
                <button className="excel-btn" onClick={handleSearch}>
                  <FontAwesomeIcon icon={faSearch} />
                </button>
              </div>

              <button className="excel-btn" onClick={exportExcel}>
                Export to Excel
              </button>
            </div>
          </div>

          <div
            style={{
              display: "flex",
              gap: "0.5rem",
              flexWrap: "wrap",
              marginBottom: "1rem",
            }}
          >
            {availableYears.map((year) => (
              <button
                key={year}
                onClick={() => {
                  setSelectedYear(year);
                  setPageIndex(0);
                }}
                className={selectedYear === year ? "active" : ""}
                style={{
                  padding: "0.45rem 0.8rem",
                  borderRadius: "999px",
                  border: "1px solid #ccc",
                  background: selectedYear === year ? "#0a7a2f" : "#fff",
                  color: selectedYear === year ? "#fff" : "#333",
                  cursor: "pointer",
                }}
              >
                {year}
              </button>
            ))}
          </div>

          <table className="biodata-table">
            <thead>
              <tr>
                <th>S/N</th>
                <th>App No</th>
                <th>Year</th>
                <th>Name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Date of Birth</th>
                <th>State of Origin</th>
                <th>Admission Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paged.length > 0 ? (
                paged.map((item, idx) => {
                  const status = item.AdmissionStatus || "Admission Processing";
                  const isAdmitted =
                    String(status).toLowerCase() === "admitted";

                  return (
                    <tr key={item.Id}>
                      <td>{start + idx + 1}</td>
                      <td>{item.ApplicationNo}</td>
                      <td>{item.ApplicationYear}</td>
                      <td>
                        {item.FirstName} {item.LastName}
                      </td>
                      <td>{item.Email}</td>
                      <td>{item.Phone}</td>
                      <td>{item.DateOfBirth}</td>
                      <td>{item.State}</td>
                      <td>
                        <span
                          style={{
                            padding: "0.3rem 0.6rem",
                            borderRadius: "999px",
                            background: isAdmitted ? "#d1fae5" : "#fef3c7",
                            color: isAdmitted ? "#065f46" : "#92400e",
                            fontWeight: 700,
                            fontSize: "0.85rem",
                          }}
                        >
                          {status}
                        </span>
                      </td>
                      <td>
                        <button onClick={() => openEdit(item)}>Edit</button>
                        <button onClick={() => handlePrint(item)}>Print</button>
                        <button
                          onClick={() => admitStudent(item.Id)}
                          disabled={isAdmitted || admittingId === item.Id}
                          style={{
                            marginLeft: "0.4rem",
                            opacity:
                              isAdmitted || admittingId === item.Id ? 0.6 : 1,
                          }}
                        >
                          {admittingId === item.Id
                            ? "Admitting..."
                            : isAdmitted
                              ? "Admitted"
                              : "Admit"}
                        </button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan="10"
                    style={{ textAlign: "center", padding: "1rem" }}
                  >
                    No applicants found for this programme/year.
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="pagination">
            <button
              onClick={() => setPageIndex((i) => Math.max(0, i - 1))}
              disabled={pageIndex === 0}
            >
              Previous
            </button>
            <span>
              Page {pageIndex + 1} of {totalPages || 1}
            </span>
            <button
              onClick={() =>
                setPageIndex((i) => Math.min(totalPages - 1, i + 1))
              }
              disabled={pageIndex >= totalPages - 1}
            >
              Next
            </button>
          </div>

          {editing && (
            <div className="overlay">
              <div className="modal edit-modal">
                {loadingEdit ? (
                  <div className="spinner">Loading…</div>
                ) : (
                  <>
                    <h2>Edit Biodata</h2>
                    <div className="section-title">Personal Information</div>

                    <div className="modal-grid">
                      {[
                        "ApplicationNo",
                        "ApplicationYear",
                        "AdmissionStatus",
                        "FirstName",
                        "LastName",
                        "MiddleName",
                        "Gender",
                        "DateOfBirth",
                        "Email",
                        "Phone",
                        "MaritalStatus",
                        "MaidenName",
                        "State",
                        "LocalGov",
                        "Nationality",
                        "Religion",
                        "Denomination",
                        "Address",
                        "Kin",
                        "KinAddress",
                        "KinPhone",
                        "Curricular",
                        "JambRegNo",
                        "JambYear",
                        "JambExamNo",
                        "JambScore",
                        "PassportUrl",
                      ].map((field) => {
                        const isDate = field === "DateOfBirth";
                        const isReadOnly =
                          field === "ApplicationNo" ||
                          field === "ApplicationYear" ||
                          field === "AdmissionStatus";

                        return (
                          <div className="field" key={field}>
                            <label>{field.replace(/([A-Z])/g, " $1")}</label>
                            <input
                              type={isDate ? "date" : "text"}
                              value={
                                isDate
                                  ? editing.DateOfBirth
                                    ? new Date(editing.DateOfBirth)
                                        .toISOString()
                                        .slice(0, 10)
                                    : ""
                                  : editing[field] || ""
                              }
                              disabled={isReadOnly}
                              onChange={(e) =>
                                setEditing({
                                  ...editing,
                                  [field]: e.target.value,
                                })
                              }
                            />
                          </div>
                        );
                      })}

                      <div className="section-title">Institutions Attended</div>
                      {(editing.institutions || []).map((inst, i) => (
                        <div className="subform-row" key={i}>
                          <input
                            placeholder="Institution Name"
                            value={inst.InstitutionName || ""}
                            onChange={(e) => {
                              const arr = [...editing.institutions];
                              arr[i].InstitutionName = e.target.value;
                              setEditing({ ...editing, institutions: arr });
                            }}
                          />
                          <input
                            placeholder="Qualification"
                            value={inst.QualificationObtained || ""}
                            onChange={(e) => {
                              const arr = [...editing.institutions];
                              arr[i].QualificationObtained = e.target.value;
                              setEditing({ ...editing, institutions: arr });
                            }}
                          />
                          <input
                            placeholder="Year From"
                            value={inst.StartYear || ""}
                            onChange={(e) => {
                              const arr = [...editing.institutions];
                              arr[i].StartYear = e.target.value;
                              setEditing({ ...editing, institutions: arr });
                            }}
                          />
                          <input
                            placeholder="Year To"
                            value={inst.EndYear || ""}
                            onChange={(e) => {
                              const arr = [...editing.institutions];
                              arr[i].EndYear = e.target.value;
                              setEditing({ ...editing, institutions: arr });
                            }}
                          />
                          <button
                            onClick={() => {
                              const arr = editing.institutions.filter(
                                (_, idx) => idx !== i,
                              );
                              setEditing({ ...editing, institutions: arr });
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      ))}

                      <button
                        className="add-btn"
                        onClick={() =>
                          setEditing({
                            ...editing,
                            institutions: [
                              ...(editing.institutions || []),
                              {
                                InstitutionName: "",
                                QualificationObtained: "",
                                StartYear: "",
                                EndYear: "",
                              },
                            ],
                          })
                        }
                      >
                        + Add Institution
                      </button>

                      <div className="section-title">Examinations Passed</div>
                      {(editing.exams || []).map((exam, examIdx) => (
                        <div className="subform-row" key={examIdx}>
                          <div
                            style={{
                              display: "flex",
                              gap: "1rem",
                              marginBottom: "0.5rem",
                              flexWrap: "wrap",
                            }}
                          >
                            <input
                              placeholder="Exam Name"
                              value={exam.ExamName || ""}
                              onChange={(e) => {
                                const arr = [...editing.exams];
                                arr[examIdx].ExamName = e.target.value;
                                setEditing({ ...editing, exams: arr });
                              }}
                            />
                            <input
                              placeholder="Year"
                              value={exam.SubjectsAndGrades?.year || ""}
                              onChange={(e) => {
                                const arr = [...editing.exams];
                                arr[examIdx].SubjectsAndGrades = {
                                  ...arr[examIdx].SubjectsAndGrades,
                                  year: e.target.value,
                                };
                                setEditing({ ...editing, exams: arr });
                              }}
                            />
                          </div>

                          {Object.entries(exam.SubjectsAndGrades || {})
                            .filter(([subj]) => subj !== "year")
                            .map(([subject, grade]) => (
                              <div className="field" key={subject}>
                                <label>{subject}</label>
                                <select
                                  value={grade || ""}
                                  onChange={(e) => {
                                    const arr = [...editing.exams];
                                    arr[examIdx].SubjectsAndGrades = {
                                      ...arr[examIdx].SubjectsAndGrades,
                                      [subject]: e.target.value,
                                    };
                                    setEditing({ ...editing, exams: arr });
                                  }}
                                >
                                  <option value="">-- select grade --</option>
                                  {gradeOptions.map((opt) => (
                                    <option key={opt} value={opt}>
                                      {opt}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            ))}

                          <button
                            onClick={() => {
                              const arr = editing.exams.filter(
                                (_, i) => i !== examIdx,
                              );
                              setEditing({ ...editing, exams: arr });
                            }}
                          >
                            Remove Exam
                          </button>
                        </div>
                      ))}

                      <button
                        className="add-btn"
                        onClick={() =>
                          setEditing({
                            ...editing,
                            exams: [
                              ...(editing.exams || []),
                              {
                                ExamName: "",
                                SubjectsAndGrades: {
                                  year: "",
                                  English: "",
                                  Mathematics: "",
                                  Biology: "",
                                  Physics: "",
                                  Chemistry: "",
                                },
                              },
                            ],
                          })
                        }
                      >
                        + Add Exam
                      </button>
                    </div>

                    <div className="modal-actions">
                      <button onClick={saveEdit} className="btn save">
                        Save
                      </button>
                      <button onClick={cancelEdit} className="btn cancel">
                        Cancel
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
